import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface RealtimeRequest {
  id: string;
  request_number: string;
  category: string;
  urgency: string;
  requester_company: string | null;
  is_matched: boolean;
  created_at: string;
}

interface MatchingEvent {
  id: string;
  request_number: string;
  status: "matching" | "matched" | "failed";
  message: string;
  timestamp: Date;
}

export function useRealtimeRequests() {
  const [recentEvents, setRecentEvents] = useState<MatchingEvent[]>([]);
  const [isListening, setIsListening] = useState(false);
  const matchingRef = useRef<Set<string>>(new Set());

  const addEvent = (event: MatchingEvent) => {
    setRecentEvents((prev) => [event, ...prev].slice(0, 50));
  };

  const triggerMatching = async (request: RealtimeRequest) => {
    // Prevent duplicate matching
    if (matchingRef.current.has(request.id)) return;
    matchingRef.current.add(request.id);

    const label = request.request_number || request.id.slice(0, 8);

    addEvent({
      id: request.id,
      request_number: label,
      status: "matching",
      message: `Matching gestartet für ${label}`,
      timestamp: new Date(),
    });

    toast.info(`🔄 Neue Anfrage ${label} – Matching läuft…`);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Nicht authentifiziert");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${supabaseUrl}/functions/v1/match-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ request_id: request.id }),
      });

      const result = await resp.json();

      if (!resp.ok) {
        throw new Error(result.error || resp.statusText);
      }

      const matchCount = result.opportunities_created ?? result.matched ?? 0;

      addEvent({
        id: request.id,
        request_number: label,
        status: "matched",
        message: `${label}: ${matchCount} Provider gematched`,
        timestamp: new Date(),
      });

      toast.success(`✅ ${label}: ${matchCount} Provider gematched`);
    } catch (err: any) {
      console.error("Auto-match error:", err);

      addEvent({
        id: request.id,
        request_number: label,
        status: "failed",
        message: `${label}: Matching fehlgeschlagen – ${err.message}`,
        timestamp: new Date(),
      });

      toast.error(`❌ ${label}: Matching fehlgeschlagen`);
    } finally {
      // Allow retry after 30s
      setTimeout(() => matchingRef.current.delete(request.id), 30_000);
    }
  };

  useEffect(() => {
    const channel = supabase
      .channel("realtime-requests")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "requests",
        },
        (payload) => {
          const newRequest = payload.new as RealtimeRequest;
          console.log("[Realtime] New request:", newRequest.id);
          triggerMatching(newRequest);
        }
      )
      .subscribe((status) => {
        setIsListening(status === "SUBSCRIBED");
        console.log("[Realtime] Subscription status:", status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { recentEvents, isListening };
}
